'use client';

import { useCallback, useEffect, useState } from "react"
import { DateTime } from "luxon"

import { useService } from "./AnonFamilyLayout";
import { useAxios, APIError } from "../../core/_axios";
import { familiesGetSignaturesForServiceByUniqueIdentifier } from "../../core/_requests";
import { ServiceSignature } from "../../core/_models";
import { useSearchParams } from "react-router-dom";
import useLocalizedCopy from "../../hooks/useLocalizedCopy";

export function FamilyGuestbook() {

    // useParams is used for URL params, not query params
    const [searchParams] = useSearchParams();

    const invitationUniqueId = searchParams.get('i');

    const {
        service
    } = useService()

    const axios = useAxios();
    const { copy } = useLocalizedCopy();

    const [errorMessage, setErrorMessage] = useState('')

    const [signedSuccessfully, setSignedSuccessfully] = useState(false)

    const [submittingSignature, setSubmittingSignature] = useState(false)

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [loadingMore, setLoadingMore] = useState(false);

    const [signatures, setSignatures] = useState<ServiceSignature[]>([]);
    const [totalSignatures, setTotalSignatures] = useState<null | number>(null);

    const itemsPerPage = 50; // set in the backend right now
    const onlyShowPreNeedItems = false;

    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    const loadSignatures = useCallback(
        async (loadPage: number = 1) => {
            const isLoadingMore = loadPage > 1;
            if (isLoadingMore) {
                setLoadingMore(true);
                setCurrentPage(loadPage);
            } else {
                setLoading(true);
                setSignatures([]); // Reset signatures when loading the first page
            }
            setLoadingError(null);
            try {
                const {
                    signatures: updatedServiceSignatures,
                    signatures_count: updatedServiceSignaturesCount,
                } = await familiesGetSignaturesForServiceByUniqueIdentifier(
                    axios,
                    service.unique_identifier,
                    loadPage,
                    invitationUniqueId
                );
                // If loadPage > 1, append signatures, otherwise set them directly
                setSignatures((prevSignatures) =>
                    isLoadingMore
                        ? [...prevSignatures, ...updatedServiceSignatures]
                        : updatedServiceSignatures
                );
                setTotalSignatures(updatedServiceSignaturesCount);
                setHasMore(updatedServiceSignatures.length === itemsPerPage);
            } catch (exception) {
                console.error(exception);
                const errorMessage =
                    exception instanceof APIError
                        ? exception.message
                        : copy.Errors.generic;
                setLoadingError(errorMessage);
            } finally {
                if (isLoadingMore) {
                    setLoadingMore(false);
                } else {
                    setLoading(false);
                }
            }
        },
        [axios, onlyShowPreNeedItems, service, invitationUniqueId]
    );

    useEffect(() => {
        loadSignatures();
    }, [loadSignatures]);

    if (signedSuccessfully) {
        return (
            <div className="fixed inset-0 flex flex-col items-center justify-center bg-white space-y-6">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                    <div className="w-8 h-8 bg-white rounded-full"></div>
                </div>
                <h2 className="text-3xl font-bold text-center text-gray-800">{copy.Public.family.thankYouMessage}</h2>
            </div>
        );
    }

    return (
        <div>
            <div className="mt-4 mb-4 flex items-center justify-center">
                <img
                    src={service.public_data.deceased_person.avatar_url}
                    alt={service.public_data.deceased_person.full_name}
                    className="object-contain object-center bg-gray-200"
                    style={{ borderRadius: "50%", overflow: 'hidden', width: 175, height: 175 }}
                    width={175}
                    height={175}
                />
            </div>
            <div className="mb-4">
                <h2 className="text-3xl font-bold text-center text-gray-800">
                    {service.public_data.deceased_person.full_name}
                </h2>
                {service.public_data.deceased_person.birth_date || service.public_data.deceased_person.death_date ? <h3 className="text-lg font-semibold text-center text-gray-600">
                    {`${service.public_data.deceased_person.birth_date ? DateTime.fromISO(service.public_data.deceased_person.birth_date).toFormat('LLL d, yyyy') : ''} - ${service.public_data.deceased_person.death_date ? DateTime.fromISO(service.public_data.deceased_person.death_date).toFormat('LLL d, yyyy') : ''}`}
                </h3> : null}
            </div>
            {loading ? <>
                <div className="text-center text-gray-600">{copy.Public.family.loading}</div>
            </> : <>
                {loadingError ? <>
                    <div className="text-center text-red-600">{loadingError}</div>
                </> : <>
                    <fieldset>
                        <legend className="border-b border-gray-500 pb-4 mb-4">
                            {copy.Public.family.virtualSignatures}
                        </legend>
                    </fieldset>
                    <div>
                        {signatures.map((signature) => (
                            <div key={signature.unique_identifier} className="flex items-center gap-4 p-4 border-b border-gray-200">
                                <div className="flex flex-col justify-center gap-2">
                                    {signature.public_data.latest_signature_transcription?.generated_json ? <>
                                        <div className="leading-none font-semibold text-sm text-gray-900 hover:text-primary cursor-pointer">
                                            {signature.public_data.latest_signature_transcription.generated_json.name || copy.Public.family.nameNotAvailable}
                                        </div>
                                        <div className="text-2sm text-gray-600">
                                            {`${signature.public_data.latest_signature_transcription.generated_json.phone_number || copy.Public.family.phoneNone} | ${signature.public_data.latest_signature_transcription.generated_json.email || copy.Public.family.emailNone}`}
                                        </div>
                                    </> : <>
                                        {signature.public_data.based_on_simple_signature ? <>
                                            <div className="leading-none font-semibold text-sm text-gray-900 hover:text-primary cursor-pointer">
                                                {signature.public_data.based_on_simple_signature.full_name || copy.Public.family.nameNotAvailable}
                                            </div>
                                            <div className="text-2sm text-gray-600">
                                                {`${signature.public_data.based_on_simple_signature.phone_number || copy.Public.family.phoneNone} | ${signature.public_data.based_on_simple_signature.email_address || copy.Public.family.emailNone}`}
                                            </div>
                                        </> : <>
                                            {/* This happens before AI transcribes the signature */}
                                            {copy.Public.family.waitingForTranscription}
                                        </>}
                                    </>}
                                </div>
                            </div>
                        ))}
                    </div>
                </>}
            </>}
        </div>
    );

}
